import type { ReactNode } from "react";
import { useEffect } from "react";
import styled from "styled-components";
import { StyleTransition } from "react-transitioning";

import { Typography } from "./Typography";
import { hexToRGB } from "../../utils/style";

/**
 * [共通パーツ]スナックバー
 */

export type SnackbarProps = {
  isOpen: boolean;
  close: () => void;
  icon: ReactNode;
  text: string | null;
};

const StyledSnackbar = styled.div`
  height: 60px;
  width: calc(100% - 40px);
  box-shadow: 0 4px 8px
    ${(props) => hexToRGB(props.theme.colors.secondaryBackground, 0.3)};
  border-radius: 6px;
  background-color: ${(props) => props.theme.colors.secondaryBackground};
  opacity: 0.95;
  color: ${(props) => props.theme.colors.secondaryText};
  display: flex;
  align-items: center;
  position: fixed;
  bottom: 20px;
  padding-left: 20px;
  padding-right: 20px;
  left: 20px;
`;

const IconWrapper = styled.div`
  margin-right: 12px;
`;

const CloseWrapper = styled.div`
  margin-left: auto;
  opacity: 0.7;
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const CloseIcon = styled.div`
  &&::after {
    display: inline-block;
    content: "\\00d7"; /* This will render the 'X' */
    margin-right: 3px;
  }
`;

const styles = {
  enter: {
    bottom: "-80px",
  },
  enterActive: {
    bottom: "20px",
    transition: "all 500ms ease-in-out",
  },
  exit: {
    opacity: 1,
  },
  exitActive: {
    opacity: 0,
    transition: "all 500ms ease-in-out",
  },
};

export function Snackbar(props: SnackbarProps) {
  const { isOpen, close, icon, text } = props;

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const timer = setTimeout(() => {
      close();
    }, 10000);
    return () => clearTimeout(timer);
  }, [isOpen, close]);

  return (
    <StyleTransition in={isOpen} duration={500} styles={styles} exit>
      <StyledSnackbar>
        <IconWrapper>{icon}</IconWrapper>
        <Typography size={3} bold>
          {text}
        </Typography>
        <CloseWrapper onClick={() => close()}>
          <CloseIcon />
          <Typography size={1} bold>
            閉じる
          </Typography>
        </CloseWrapper>
      </StyledSnackbar>
    </StyleTransition>
  );
}
